import { Container, Graphics, Text, BlurFilter, FillGradient } from "pixi.js"
import gsap from "gsap";

import Image from "./Image";
import { canvasSize, colors, getConstants } from "./Config";
import { State } from "@/store/modules/useChapterState";
import Viewport from "@/store/modules/Viewport";
import lerp from "@/core/lerp";

const DEBUG = false;
const scrollEase = gsap.parseEase("power3.out");
const scrollZoomEase = gsap.parseEase("power1.out");

export type ChapterData = {
  hero: {
    title: string;
    image: {
      desktop: string;
      mobile: string;
    };
  };
  content: any
}

export default class Chapter {

  public index: number;
  public width: number;
  public height: number;
  public container: Container;
  public containerIntro: Container;
  public containerScroll: Container;
  // public containerSwipe: Container;
  public containerScrollZoom: Container;
  public dragger: Container;
  public graphic: Graphics;
  public image: Image;
  public imageWrapper: Container;
  public imageScrollZoom: Container;
  public blurFilter: BlurFilter | null = null;
  public gradient: Graphics;

  public debug: Graphics | null = null;
  
  private _x: number;
  private _scrollScale: number;
  private _scrollScaleMax: number;
  
  private transparentTimeline: gsap.core.Timeline | null;
  private isTransparent: boolean = false;

  public timeline: gsap.core.Timeline | null;

  constructor(i: number = 0, data: ChapterData, state: State, isClone: boolean) {
    this.index = i;

    const constants = getConstants();

    const container = new Container();
    const dragger = new Container();
    const containerIntro = new Container();
    const containerScroll = new Container();
    // const containerSwipe = new Container();
    const containerScrollZoom = new Container();
    // container.addChild(containerSwipe);
    // containerSwipe.addChild(containerScroll);
    container.addChild(containerScroll);
    containerScroll.addChild(containerIntro);
    containerIntro.addChild(dragger);
    dragger.addChild(containerScrollZoom);


    const baseW = constants.bigWidth;
    const baseH = baseW / constants.ratio.big;

    const graphic = new Graphics();
  
    graphic.beginPath();
    graphic.rect(0, 0, baseW, baseH);
    graphic.fill(colors[this.index % colors.length]);
    // graphic.fill(0xffffff);
    graphic.closePath();


    // const background = new Graphics();
    // background.beginPath();
    // background.rect(0, 0, baseW, baseH);
    // // background.fill(colors[index % colors.length]);
    // background.fill(0xff0000);
    // background.alpha = 0.5;
    // background.closePath();
    // // background.mask = graphic;
    // containerScrollZoom.addChild(background);
  
    // IMAGES
    this.imageWrapper = new Container();
    this.imageScrollZoom = new Container();
    // IMAGE BASE
    const image = new Image({
      url: Viewport.isMobile ? data.hero.image.mobile : data.hero.image.desktop,
      width: baseW,
      height: baseH,
      // crop: false,
    });
    this.imageWrapper.addChild(image);
    this.imageScrollZoom.addChild(this.imageWrapper);
    
    // imageBase.mask = graphic;
    // image.alpha = 0;
    

    this.imageScrollZoom.mask = graphic;


    containerScrollZoom.addChild(this.imageScrollZoom);
    containerScrollZoom.addChild(graphic);


    this.width = baseW;
    this.height = baseH;
    this.container = container;
    this.containerIntro = containerIntro;
    // this.containerSwipe = containerSwipe;
    this.containerScroll = containerScroll;
    this.containerScrollZoom = containerScrollZoom;
    this.dragger = dragger;
    this.graphic = graphic;
    this.image = image;
    this._x = 0;
    this._scrollScale = 1;
  

    this.blurFilter = new BlurFilter();
    this.blurFilter.blur = 0;
    // this.container.filters = [this.blurFilter];

    this.createGradient(baseW, baseH);
    this.update(state, true);

    if (DEBUG) {
      this.createDebug(isClone);
    }

    this.resizePivots();
  }

  updateBlur = () => {
    if (this.blurFilter.blur > 0) {
      this.container.filters = [this.blurFilter];
    } else {
      this.container.filters = [];
    }
  }

  createGradient(width: number, height: number) {
    

    this.gradient = new Graphics();
    this.gradient.alpha = 1;
    this.drawGradient(width, height);
    

    this.imageWrapper.addChild(this.gradient);

    this.gradient.blendMode = 'multiply';
    this.gradient.alpha = 0;
  }
  drawGradient(width: number, height: number) {
    const gradientFill = new FillGradient(width/2, 0, width/2, height);
    gradientFill.addColorStop(0, 0xffffff);
    gradientFill.addColorStop(1, 0x000000);

    this.gradient.beginPath();
    this.gradient.rect(0, 0, width + 16, height + 16);
    this.gradient.fill(gradientFill);
    this.gradient.closePath();
  }

  createDebug(isClone: boolean) {
    const debug = new Graphics();
    debug.alpha = 1;
    this.dragger.addChild(debug);

    debug.beginPath();
    debug.rect(0, 0, 60, 60);
    debug.fill(isClone ? 0x333333 : 0xffffff);
    debug.closePath();
    debug.x = this.width / 2 - 30;
    debug.y = -15;

    const text = new Text(
      {
        text: this.index - canvasSize.offsetIndex + 1,
        style: {
            fontFamily: 'Arial',
            fontSize: 24,
            fill: !isClone ? 0x000000 : 0xffffff,
            align: 'center',
        }
      }
    )
    text.anchor.set(0.5);
    text.x = 30;
    text.y = 30;
    debug.addChild(text);

    this.debug = debug;
  }
  
  resizePivots() {
    const constants = getConstants();

    const baseW = constants.bigWidth;
    const baseH = baseW / constants.ratio.big;

    this.containerScrollZoom.pivot.set(baseW / 2, baseH / 2);
    this.containerScrollZoom.x = baseW / 2;
    this.containerScrollZoom.y = baseH / 2;

    // this.containerIntro.pivot.set(baseW / 2, baseH / 2);
    // this.containerIntro.x = baseW / 2;
    // this.containerIntro.y = baseH / 2;

    this._scrollScaleMax = Viewport.isDesktop ? Viewport.windowWidth * 0.66 / baseW : Viewport.windowWidth / baseW;

    this.graphic.pivot.set(baseW / 2, baseH / 2);
    this.graphic.x = baseW / 2;
    this.graphic.y = baseH / 2;

    this.image.pivot.set(baseW / 2, baseH / 2);
    this.image.x = baseW / 2;
    this.image.y = baseH / 2;

    this.imageScrollZoom.pivot.set(baseW / 2, baseH / 2);
    this.imageScrollZoom.x = baseW / 2;
    this.imageScrollZoom.y = baseH / 2;

    this.gradient.pivot.set(baseW / 2, baseH / 2);
    this.gradient.x = baseW / 2;
    this.gradient.y = baseH / 2;
  }
  resize () {
    const { graphic, image, index, gradient, debug } = this;
    const constants = getConstants();

  
    const baseW = constants.bigWidth;
    const baseH = baseW / constants.ratio.big;
  
    graphic.clear();
    graphic.beginPath();
    graphic.rect(0, 0, baseW, baseH);
    graphic.fill(colors[index % colors.length]);
    graphic.closePath();

    gradient.clear();
    this.drawGradient(baseW, baseH);

    this.resizePivots();
    image.updateSize(baseW, baseH);
  
    this.width = baseW;
    this.height = baseH;

    if (DEBUG) {
      debug.x = this.width / 2 - 30;
      debug.y = -15;
    }
  }

  static getActiveIndex = (state: State): number => {
    return state.activeIndex + canvasSize.offsetIndex;
  }
  
  update (state: State, instant: boolean = false) {
    const constants = getConstants();
    const activeIndex = Chapter.getActiveIndex(state);
  
    const isSmall = (state.isListing || this.index !== activeIndex);
    const duration = instant ? 0 : ((state.isListing) ? (state.isGrabbing ? 0.2 : 0.3) : 0.7);
    const smallDuration = duration * ((!state.isListing && this.index === activeIndex) ? 1.1 : 0.8);

    const scaleX = isSmall ? constants.smallScale.x : 1;
    const scaleY = isSmall ? constants.smallScale.y : 1;

    const smallFactor = (!state.isListing && this.index !== activeIndex && Viewport.isDesktop) ? 0.65 : 1;
    const zoomSmallFactor = (!state.isListing && this.index !== activeIndex && Viewport.isDesktop) ? 0.8 : 1;
    
    if (this.timeline) {
      this.timeline.kill();
    }
  
    this.timeline = gsap.timeline({ bigs: {
      duration,
      ease: "quart.inOut",
    }});
    this.timeline.to(
      this.graphic.scale,
      {
        x: scaleX * smallFactor,
        y: scaleY * smallFactor,
        duration: smallDuration
      },
      0
    );
    // this.timeline.to(
    //   this.graphic,
    //   {
    //     y: scaleY === 1 ? 0 : (this.height / 2) * (1 - scaleY * smallFactor),
    //     x: isSmall ? (-constants.smallWidth * smallFactor + constants.bigWidth) / 2 : 0,
    //     duration: smallDuration
    //   },
    //   0
    // )
  
    const needZoomSprite = state.isListing || this.index !== activeIndex;
    this.timeline.to(
      this.image.scale,
      {
        x: needZoomSprite ? constants.spriteScale * (zoomSmallFactor) : 1,
        y: needZoomSprite ? constants.spriteScale * (zoomSmallFactor) : 1,
        duration: needZoomSprite ? smallDuration : smallDuration * 1.2
      },
      0
    )


    const needBlur = (!state.isListing && this.index !== activeIndex);
    this.timeline.to(
      this.blurFilter,
      {
        blur: needBlur ? 8 : 0,
        duration: smallDuration,
        onUpdate: this.updateBlur,
      },
      0
    )
    this.timeline.to(
      this.container,
      {
        alpha: needBlur ? 0.5 : 1,
        duration: smallDuration
      },
      0
    )
    this.timeline.to(
      this.gradient,
      {
        alpha: (!state.isListing && this.index === activeIndex) ? 0.7 : 0,
        duration: smallDuration
      },
      0
    )
    // this.timeline.to(
    //   this.imageBlur,
    //   {
    //     alpha: (state.isListing || this.index === activeIndex) ? 0 : 1,
    //     duration: smallDuration
    //   },
    //   0
    // )
  
    this.container.y = (canvasSize.height + canvasSize.offsetVertical) / 2 - this.height / 2;
    let x = 0;
    let delay = 0;
    if (state.isListing) {
      this.container.alpha = 1;
      x = (this.width * scaleX + constants.gap) * this.index + (canvasSize.width - constants.bigWidth) / 2 ;
  
      if (this.index < activeIndex) {
        const beforeIndex = activeIndex - this.index - 1;
        delay = constants.staggerGap * Math.min(beforeIndex, 3);
      } else {
        const afterIndex = this.index - activeIndex - 1;
        delay = constants.staggerGap * Math.min(afterIndex, 3);
      }

      x -= canvasSize.offsetIndex * (this.width * scaleX + constants.gap);
    } else {
      if (this.index === activeIndex) {
        x = (canvasSize.width - this.width) / 2;
        // console.log('Zoom in', { index: this.index - canvasSize.offsetIndex, dragX: this.dragger.x })
      } else {
        if (this.index < activeIndex) {
          const beforeIndex = activeIndex - this.index - 1;
  
          const startAt = - this.width / 2 - this.width * scaleX / 2 + constants.offset;
          x = startAt - ((this.width * scaleX + constants.gap) * beforeIndex);
        } else {
          const afterIndex = this.index - activeIndex - 1;
  
          const startAt = canvasSize.width - this.width / 2 + this.width * scaleX / 2 - constants.offset;
          x = startAt + ((this.width * scaleX + constants.gap) * afterIndex);
        }
      }
      // Remove dragger X
      x -= this.dragger.x;
    }
  
    this.timeline.to(
      this, 
      {
        _x: x,
        duration: state.isListing ? smallDuration * 0.5 : smallDuration,
      },
      delay
    )
  }
  
  tick = (state: State, dragX: number) => {
    const { loopSize } = canvasSize;
    const constants = getConstants();
    
    if (state.isListing) {
      const roundedDragX = Math.round(dragX * 100) / 100;
      const loopedX = Chapter.getLoopedX(roundedDragX, loopSize);

      this.dragger.x = loopedX;
      const progress = (loopedX + (this.index - canvasSize.offsetIndex) * (constants.smallWidth + constants.gap)) / loopSize;
      this.imageWrapper.x = (progress * constants.smallWidth) * 0.333;
    }
    this.container.x = this._x;

    const activeIndex = Chapter.getActiveIndex(state);

    const scrollProgress = Viewport.isDesktop ? state.scrollProgress : 0;
    const easeScrollProgress = scrollEase(scrollProgress);
    const easeScrollZoomProgress = scrollZoomEase(scrollProgress);
    const direction = (state.isListing || this.index === activeIndex) ? 0 : this.index < activeIndex ? -1 : 1;

    const scrollTarget = constants.offset * easeScrollProgress * direction;
    // const panDirection = (!state.isListing && state.allowSwipe) ? (state.swipeX > 0 ? 1 : -1) : 0;
    // const panTarget = scrollEase(Math.abs(state.swipeX)) * Viewport.windowWidth/24 * panDirection;
    this.containerScroll.x = lerp(scrollTarget, this.containerScroll.x, 0.1);
    // this.containerSwipe.x = lerp(panTarget, this.containerIntro.x, state.isListing ? 0.9 : 0.3);

    const scrollScaleTarget = (state.isListing || state.isLoading || this.index !== activeIndex) ? 1 : 1 + (this._scrollScaleMax - 1) * easeScrollZoomProgress;
    this._scrollScale = lerp(scrollScaleTarget, this._scrollScale, state.isListing ? 0.01 : 0.3)
    
    const imageScale = 1/(1 + (this._scrollScale - 1) * 0.4);
    // console.log('scrollScale', { scrollScale: this._scrollScale, imageScale })
    this.containerScrollZoom.scale.set(this._scrollScale);
    this.imageScrollZoom.scale.set(imageScale);
    this.gradient.scale.set(this._scrollScale);

    this.updateTransparency(state);
  }

  updateTransparency = (state: State) => {
    const isCurrent = this.isCurrentChapter(state);
    const isTransparent = state.isListing && state.isGrabbing && !isCurrent;

    if (this.isTransparent !== isTransparent) {

      this.transparentTimeline?.kill();
      this.transparentTimeline = gsap.timeline()
      this.transparentTimeline.to(
        this.image,
        {
          alpha: isTransparent ? 0.6 : 1,
          duration: 0.3,
          ease: 'quart.out'
        },
        0
      )
      if (state.isGrabbing || isCurrent) {
        this.transparentTimeline.to(
          this.blurFilter,
          {
            blur: isTransparent ? 4 : 0,
            duration: 0.3,
            ease: 'quart.out',
            onUpdate: this.updateBlur,
          },
          0
        )
      }
      this.isTransparent = isTransparent;
    }
  }

  isCurrentChapter = (state: State) => {
    const activeIndex = Chapter.getActiveIndex(state);
    const isCurrent = 
      this.index === activeIndex || this.index - canvasSize.loopNbItem === activeIndex || this.index + canvasSize.loopNbItem === activeIndex;
    
    // if (isCurrent) {
    //   console.log('isCurrentChapter', {
    //     activeIndex: state.activeIndex,
    //     // loopNbItem: canvasSize.loopNbItem,
    //     // index: this.index,
    //   })
    // }

    return isCurrent;
  }

  static getLoopedX = (x: number, loopSize: number): number => {
    let loopedX = x % loopSize - (x > 0 ? loopSize : 0);
    if (loopedX === loopSize || loopedX === loopSize * -1) {
      loopedX = 0;
    }

    return loopedX;
  }

  static getLoopIndex = (x: number, loopSize: number): number => {
    return (Math.round(x / loopSize) - (x < 0 ? 1 : 0)) + 1;
  }
}